import React from "react";

interface AddEditLabModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingLab: { source?: string } | null;
  formData: {
    name: string;
    address_line_1: string;
    address_line_2: string;
    suite: string;
    city: string;
    state_province: string;
    country: string;
    postal_code: string;
    telephone: string;
    email: string;
    cpsc_id: string;
    status: "active" | "inactive";
  };
  formErrors: Record<string, string>;
  saving: boolean;
  handleInputChange: (field: string, value: string) => void;
  handleSaveTestLab: () => void;
  onAddLab?: (lab: any) => void; // ✅ optional prop for call from AskRexi.tsx
}

const AddEditLabModal = (props: AddEditLabModalProps) => {
  const {
    isOpen,
    onClose,
    editingLab,
    formData,
    formErrors,
    saving,
    handleInputChange,
    handleSaveTestLab,
    onAddLab,
  } = props;

  if (!isOpen) return null;

  return (
    <div className="data-modal-overlay" onClick={onClose}>
      <div className="data-modal" onClick={(e) => e.stopPropagation()}>
        <div className="data-modal-header">
          <h2 className="data-modal-title">
            {editingLab ? "Test Lab Details" : "Add New Test Lab"}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="data-modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Lab Name <span className="required-indicator">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter lab name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                readOnly={editingLab?.source === "certified"}
              />
              {formErrors.name && (
                <div className="form-error">{formErrors.name}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Address Line 1</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter address line 1"
                value={formData.address_line_1}
                onChange={(e) =>
                  handleInputChange("address_line_1", e.target.value)
                }
                readOnly={editingLab?.source === "certified"}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Address Line 2</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter address line 2"
                value={formData.address_line_2}
                onChange={(e) =>
                  handleInputChange("address_line_2", e.target.value)
                }
                readOnly={editingLab?.source === "certified"}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Suite</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter suite number"
                value={formData.suite}
                onChange={(e) => handleInputChange("suite", e.target.value)}
                readOnly={editingLab?.source === "certified"}
              />
            </div>

            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter city"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                readOnly={editingLab?.source === "certified"}
              />
            </div>

            <div className="form-group">
              <label className="form-label">State/Province</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter state or province"
                value={formData.state_province}
                onChange={(e) =>
                  handleInputChange("state_province", e.target.value)
                }
                readOnly={editingLab?.source === "certified"}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Country</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter country"
                value={formData.country}
                onChange={(e) => handleInputChange("country", e.target.value)}
                readOnly={editingLab?.source === "certified"}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Postal Code</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter postal code"
                value={formData.postal_code}
                onChange={(e) =>
                  handleInputChange("postal_code", e.target.value)
                }
                readOnly={editingLab?.source === "certified"}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                className="form-input"
                placeholder="Enter phone number"
                value={formData.telephone}
                onChange={(e) => handleInputChange("telephone", e.target.value)}
                readOnly={editingLab?.source === "certified"}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                readOnly={editingLab?.source === "certified"}
              />
              {formErrors.email && (
                <div className="form-error">{formErrors.email}</div>
              )}
            </div>
{/*
            <div className="form-group">
              <label className="form-label">CPSC ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter CPSC ID (optional)"
                value={formData.cpsc_id}
                onChange={(e) => handleInputChange("cpsc_id", e.target.value)}
                readOnly={editingLab?.source === "certified"}
              />
            </div>
*/}
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                disabled={editingLab?.source === "certified"}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="data-modal-footer">
          <button
            className="modal-btn modal-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            {editingLab?.source === "certified" ? "Close" : "Cancel"}
          </button>
          {editingLab?.source !== "certified" && (
            <button
              className="modal-btn modal-btn-save"
              onClick={async () => {
                await handleSaveTestLab();
                if (onAddLab && formData.name?.trim()) {
                  // ✅ notify parent (AskRexi) which lab was added
                  onAddLab({
                    name: formData.name,
                    address_line_1: formData.address_line_1,
                    city: formData.city,
                    country: formData.country,
                    source: "user",
                  });
                }
                onClose();
              }}
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : editingLab
                  ? "Update Test Lab"
                  : "Add Test Lab"}
            </button>

          )}
        </div>
      </div>
    </div>
  );
};

export default AddEditLabModal;
